// Auto-generated by scripts/split_calculators.py — keeps deterministic oilfield formulas in per-category modules.
import { round, num, positive, toRad } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, toRad };

// ===== Category: production =====
// --- Production ---
// =========================================================================
// PRODUCTION
// =========================================================================

export function waterCut({ waterBpd, oilBpd }) {
  const W = num(waterBpd, "Water"); if (W < 0) throw new Error("Water cannot be negative");
  const O = num(oilBpd, "Oil"); if (O < 0) throw new Error("Oil cannot be negative");
  if (W + O === 0) throw new Error("Total liquid must be > 0");
  const wc = (W / (W + O)) * 100;
  return {
    result: round(wc, 2),
    unit: "%",
    formula: "WC = Water / (Water + Oil) × 100",
    steps: [
      `WC = ${W} / (${W}+${O}) × 100 = ${round(wc, 2)}%`,
    ],
    inputs: { "Water (bbl/d)": W, "Oil (bbl/d)": O },
  };
}

export function gasOilRatio({ gasScf, oilBbl }) {
  const G = positive(num(gasScf, "Gas (scf)"), "Gas");
  const O = positive(num(oilBbl, "Oil (bbl)"), "Oil");
  const gor = G / O;
  return {
    result: round(gor, 1),
    unit: "scf/bbl",
    formula: "GOR = Gas (scf) / Oil (bbl)",
    steps: [`GOR = ${G} / ${O} = ${round(gor, 1)} scf/bbl`],
    inputs: { "Gas (scf)": G, "Oil (bbl)": O },
  };
}

export function barrelsPerDay({ barrels, hours }) {
  const B = positive(num(barrels, "Barrels"), "Barrels");
  const H = positive(num(hours, "Hours"), "Hours");
  const bpd = (B / H) * 24;
  return {
    result: round(bpd, 1),
    unit: "BPD",
    formula: "BPD = (B / H) × 24",
    steps: [`BPD = (${B}/${H}) × 24 = ${round(bpd, 1)} BPD`],
    inputs: { "Barrels": B, "Hours": H },
  };
}

export function downtimeProductionLoss({ bpd, downtimeHours }) {
  const Q = positive(num(bpd, "BPD"), "BPD");
  const T = positive(num(downtimeHours, "Downtime (hr)"), "Downtime");
  const loss = Q * T / 24;
  return {
    result: round(loss, 2),
    unit: "bbl",
    formula: "Loss = BPD × Downtime(hr) / 24",
    steps: [`Loss = ${Q} × ${T} / 24 = ${round(loss, 2)} bbl`],
    inputs: { "BPD": Q, "Downtime (hr)": T },
  };
}

// --- Production Engineering ---
// =========================================================================
// PRODUCTION ENGINEERING — Vogel IPR, Productivity Index, ESP sizing
// =========================================================================

export function vogelIPR({ reservoirPressure, testFlowRate, testPwf, targetPwf }) {
  const Pr = positive(num(reservoirPressure, "Reservoir Pressure"), "Reservoir Pressure");
  const qt = positive(num(testFlowRate, "Test Flow Rate"), "Test Flow Rate");
  const Pwft = num(testPwf, "Test Pwf"); if (Pwft < 0) throw new Error("Test Pwf must be ≥ 0");
  if (Pwft >= Pr) throw new Error("Test Pwf must be less than reservoir pressure");
  const Pwf = num(targetPwf, "Target Pwf"); if (Pwf < 0) throw new Error("Target Pwf must be ≥ 0");
  if (Pwf > Pr) throw new Error("Target Pwf cannot exceed reservoir pressure");

  const rt = Pwft / Pr;
  const denom = 1 - 0.2 * rt - 0.8 * rt * rt;
  if (denom <= 0) throw new Error("Test point yields non-physical qmax — check inputs");
  const qmax = qt / denom;
  const r = Pwf / Pr;
  const q = qmax * (1 - 0.2 * r - 0.8 * r * r);

  return {
    result: round(q, 1),
    unit: "BPD",
    formula: "Vogel: q/qmax = 1 − 0.2·(Pwf/Pr) − 0.8·(Pwf/Pr)²",
    steps: [
      `Test point: q_t=${qt} BPD at Pwf_t=${Pwft} psi (Pr=${Pr} psi)`,
      `Test ratio = ${Pwft}/${Pr} = ${round(rt, 4)}`,
      `q_max = ${qt} / (1 − 0.2·${round(rt, 4)} − 0.8·${round(rt, 4)}²) = ${round(qmax, 1)} BPD (AOF)`,
      `Target ratio = ${Pwf}/${Pr} = ${round(r, 4)}`,
      `q = ${round(qmax, 1)} × (1 − 0.2·${round(r, 4)} − 0.8·${round(r, 4)}²) = ${round(q, 1)} BPD`,
    ],
    inputs: { "Pr (psi)": Pr, "q_test (BPD)": qt, "Pwf_test (psi)": Pwft, "Pwf_target (psi)": Pwf },
    extras: {
      "AOF / q_max (BPD)": round(qmax, 1),
      "Test ratio Pwf_t/Pr": round(rt, 4),
      "Drawdown (psi)": round(Pr - Pwf, 0),
    },
  };
}

export function productivityIndex({ flowRate, reservoirPressure, flowingPressure }) {
  const q = positive(num(flowRate, "Flow Rate"), "Flow Rate");
  const Pr = positive(num(reservoirPressure, "Reservoir Pressure"), "Reservoir Pressure");
  const Pwf = num(flowingPressure, "Flowing BHP"); if (Pwf < 0) throw new Error("Pwf must be ≥ 0");
  if (Pwf >= Pr) throw new Error("Flowing BHP must be less than reservoir pressure");
  const dd = Pr - Pwf;
  const J = q / dd;
  return {
    result: round(J, 4),
    unit: "BPD/psi",
    formula: "J = q / (Pr − Pwf)   (straight-line IPR, single-phase)",
    steps: [
      `Drawdown = ${Pr} − ${Pwf} = ${dd} psi`,
      `J = ${q} / ${dd} = ${round(J, 4)} BPD/psi`,
    ],
    inputs: { "q (BPD)": q, "Pr (psi)": Pr, "Pwf (psi)": Pwf },
    extras: { "Drawdown (psi)": dd },
  };
}

export function espTDH({ workingFluidLevel, frictionLoss, wellheadPressure, fluidSG }) {
  const L = positive(num(workingFluidLevel, "Working Fluid Level"), "Working Fluid Level");
  const F = num(frictionLoss, "Friction Loss"); if (F < 0) throw new Error("Friction loss must be ≥ 0");
  const Pwh = num(wellheadPressure, "Wellhead Pressure"); if (Pwh < 0) throw new Error("Wellhead pressure must be ≥ 0");
  const SG = positive(num(fluidSG, "Fluid SG"), "Fluid SG");
  // Convert WHP to head: head_ft = Pwh × 2.31 / SG
  const pHead = (Pwh * 2.31) / SG;
  const tdh = L + F + pHead;
  return {
    result: round(tdh, 1),
    unit: "ft (TDH)",
    formula: "TDH = WFL + Friction + (WHP × 2.31 / SG)",
    steps: [
      `WFL=${L} ft, Friction=${F} ft, WHP=${Pwh} psi, SG=${SG}`,
      `Pressure head = (${Pwh} × 2.31) / ${SG} = ${round(pHead, 1)} ft`,
      `TDH = ${L} + ${F} + ${round(pHead, 1)} = ${round(tdh, 1)} ft`,
    ],
    inputs: { "WFL (ft)": L, "Friction (ft)": F, "WHP (psi)": Pwh, "SG": SG },
    extras: { "Pressure Head (ft)": round(pHead, 1) },
  };
}

export function espHydraulicHP({ flowRate, tdh, fluidSG, efficiency }) {
  const q = positive(num(flowRate, "Flow Rate"), "Flow Rate"); // BPD
  const H = positive(num(tdh, "TDH"), "TDH"); // ft
  const SG = positive(num(fluidSG, "Fluid SG"), "Fluid SG");
  const eff = positive(num(efficiency, "Pump Efficiency"), "Pump Efficiency");
  if (eff > 1) throw new Error("Pump efficiency must be ≤ 1 (use fraction, e.g. 0.65)");
  // Hydraulic HP = (q [BPD] × TDH [ft] × SG) / 135828
  const hhp = (q * H * SG) / 135828;
  const bhp = hhp / eff;
  return {
    result: round(bhp, 2),
    unit: "BHP",
    formula: "HHP = (q·TDH·SG)/135828;  BHP = HHP / η",
    steps: [
      `q=${q} BPD, TDH=${H} ft, SG=${SG}, η=${eff}`,
      `HHP = (${q} × ${H} × ${SG}) / 135828 = ${round(hhp, 2)} HP`,
      `BHP = ${round(hhp, 2)} / ${eff} = ${round(bhp, 2)} HP`,
    ],
    inputs: { "q (BPD)": q, "TDH (ft)": H, "SG": SG, "η": eff },
    extras: { "Hydraulic HP": round(hhp, 2) },
  };
}

export function nodalDeliverability({ reservoirPressure, productivityIndex, targetPwf }) {
  // Simple intersection: with constant J (straight-line IPR), q = J × (Pr − Pwf)
  // For a given target Pwf (system intake), find q. This is a degenerate nodal point.
  const Pr = positive(num(reservoirPressure, "Reservoir Pressure"), "Reservoir Pressure");
  const J = positive(num(productivityIndex, "Productivity Index"), "Productivity Index");
  const Pwf = num(targetPwf, "Target Pwf"); if (Pwf < 0) throw new Error("Pwf must be ≥ 0");
  if (Pwf >= Pr) throw new Error("Pwf must be less than Pr");
  const q = J * (Pr - Pwf);
  const aof = J * Pr;
  return {
    result: round(q, 1),
    unit: "BPD",
    formula: "q = J × (Pr − Pwf);  AOF = J × Pr",
    steps: [
      `Drawdown = ${Pr} − ${Pwf} = ${Pr - Pwf} psi`,
      `q = ${J} × ${Pr - Pwf} = ${round(q, 1)} BPD`,
      `AOF (Pwf=0) = ${J} × ${Pr} = ${round(aof, 1)} BPD`,
    ],
    inputs: { "Pr (psi)": Pr, "J (BPD/psi)": J, "Pwf (psi)": Pwf },
    extras: { "AOF (BPD)": round(aof, 1) },
  };
}

// --- v1.4 — Production additions ---
// ----- Production additions -----

export function oilCut({ oilBpd, waterBpd }) {
  const O = num(oilBpd, "Oil"); if (O < 0) throw new Error("Oil cannot be negative");
  const W = num(waterBpd, "Water"); if (W < 0) throw new Error("Water cannot be negative");
  if (O + W === 0) throw new Error("Total liquid must be > 0");
  const oc = (O / (O + W)) * 100;
  return {
    result: round(oc, 2),
    unit: "%",
    formula: "Oil Cut = Oil / (Oil + Water) × 100",
    steps: [`${O} / (${O}+${W}) × 100 = ${round(oc, 2)}%`],
    inputs: { "Oil (bbl/d)": O, "Water (bbl/d)": W },
  };
}

export function tankVolumeCylindrical({ diameter, height }) {
  const D = positive(num(diameter, "Diameter"), "Diameter");
  const H = positive(num(height, "Fluid Height"), "Fluid Height");
  const r = D / 2;
  const vFt3 = Math.PI * r * r * H;
  const vBbl = vFt3 / 5.615;
  const vM3 = vFt3 * 0.0283168;
  return {
    result: round(vBbl, 2),
    unit: "bbl",
    formula: "V = π × r² × h ;  ft³ → bbl (÷5.615), ft³ → m³ (×0.02832)",
    steps: [
      `r = ${D}/2 = ${round(r, 3)} ft, h = ${H} ft`,
      `V = π × ${round(r, 3)}² × ${H} = ${round(vFt3, 2)} ft³`,
      `V = ${round(vBbl, 2)} bbl = ${round(vM3, 3)} m³`,
    ],
    inputs: { "Diameter (ft)": D, "Height (ft)": H },
    extras: { "Volume (ft³)": round(vFt3, 2), "Volume (m³)": round(vM3, 3) },
  };
}

export function pumpingRate({ volume, time }) {
  const V = positive(num(volume, "Volume"), "Volume");
  const T = positive(num(time, "Time"), "Time");
  const r = V / T;
  return {
    result: round(r, 3),
    unit: "vol/time",
    formula: "Rate = Volume / Time",
    steps: [`${V} / ${T} = ${round(r, 3)} per time unit`],
    inputs: { Volume: V, Time: T },
  };
}

export function chemicalInjectionRate({ dose, productionRate }) {
  const D = positive(num(dose, "Dose"), "Dose");
  const R = positive(num(productionRate, "Production Rate"), "Production Rate");
  const r = D * R;
  return {
    result: round(r, 4),
    unit: "chemical/time",
    formula: "Injection Rate = Dose × Production Rate",
    steps: [`${D} × ${R} = ${round(r, 4)} (use consistent units)`],
    inputs: { Dose: D, "Production Rate": R },
  };
}


